import { useState } from 'react';
import { CustomDialogProps } from '@/dashboard/components/Dialog/index';
import LogoLoading from '@/dashboard/components/loading/LogoLoading';
import PolicyModel from '@/dashboard/models/policys/PolicyModel';
import { DatePickerControlled } from '@/dashboard/mui/form/DatePicker';
import { usePolicyStore } from '@/dashboard/store/PolicyStore';
import { PlayIcon } from '@heroicons/react/outline';
import { apiUnpausePolicy, convertDayjsTimeToServerStoredTimeData, judgeWhetherIsBetweenTwoDate, judgeWhetherIsEarlierThanTheDate } from '@jove-frontend/uikit';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Typography } from '@mui/material';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { cloneDeep } from 'lodash';
import { FormProvider, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

type UnPausePolicyViewProps = CustomDialogProps & {
  policy: PolicyModel;
};

const UnPausePolicyView = (props: UnPausePolicyViewProps) => {
  const [loading, setLoading] = useState(false);
  const { className, policy, close } = props;
  const [canDoNowDate, setCanDoNowDate] = useState(judgeWhetherIsEarlierThanTheDate(dayjs(new Date()), policy.policyStartDateDayjs) ? policy.policyStartDateDayjs : dayjs(new Date()));
  const methods = useForm({
    defaultValues: { endDate: canDoNowDate.add(1, 'day') },
  });
  const { handleSubmit, register, control, getValues } = methods;
  const [{ fetchPolicies }] = usePolicyStore(({ actions }) => [actions]);
  const [minStartDate, setMinStartDate] = useState(cloneDeep(canDoNowDate));

  const closeAction = () => {
    if (close) close();
  };

  /**
   * requests
   */
  const requestUnpausePolicy = async () => {
    const params = {
      pause_id: policy?.pauses?.[0]?.id,
      pause_end_date: convertDayjsTimeToServerStoredTimeData(getValues('endDate')),
    };

    setLoading(true);
    const [res] = await apiUnpausePolicy(params);
    setLoading(false);
    if (res) {
      fetchPolicies();
      toast.success('Unpause success!');
      closeAction();
    }
  };

  /**
   * methods
   */

  // click unpause
  const unPauseAction = () => {
    handleSubmit(requestUnpausePolicy)();
  };

  const endTimeValidate = (time) => {
    return judgeWhetherIsBetweenTwoDate(time, canDoNowDate.add(1, 'day').startOf('day'), policy.pauseEndDateDayjs) ? true : 'Please select a valid date';
  };

  return (
    <FormProvider {...methods}>
      <form className="relative">
        {loading && <LogoLoading />}
        <Box className={clsx('flex flex-col items-center', className)}>
          <Box className="bg-brand-secondary/20 text-brand-secondary rounded-full p-2">
            <PlayIcon className="h-7 w-7" />
          </Box>
          <Typography className="text-black-secondary mt-4 font-black" variant="h5">
            Unpause your policy
          </Typography>
          <Typography className="text-black-secondary mt-3" variant="subtitle1">
            You will get back on premium from the date selected.
          </Typography>
          <Typography className="text-black-secondary mt-8 font-bold" variant="subtitle1">
            When do you want to unpause your policy?
          </Typography>
          <DatePickerControlled
            className="mt-4 w-full"
            name="endDate"
            control={control}
            {...register('endDate', {
              required: true,
              validate: endTimeValidate,
            })}
            minDate={minStartDate}
            maxDate={policy.pauseEndDateDayjs}
            label="End date"
          />
          <Box className="mt-6 flex w-full flex-row justify-end">
            <Button className="text-brand-primary text-lg font-bold" variant="text" onClick={closeAction}>
              Keep pausing
            </Button>
            <LoadingButton className="ml-2 h-12 w-[174px] rounded-full text-lg font-bold" color="primary" onClick={unPauseAction} loading={loading} loadingIndicator="Loading..." variant="contained">
              <span>Unpause policy</span>
            </LoadingButton>
          </Box>
        </Box>
      </form>
    </FormProvider>
  );
};

export default UnPausePolicyView;
